import React, { useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-10% 0px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 0,
    y: 20
  }} transition={{
    duration: 0.6,
    delay,
    ease: 'easeOut'
  }} data-id="element-307">
      {children}
    </motion.div>;
};
export function NarrativeSection() {
  return <section className="w-full bg-[#050505] text-[#f5f0e8] pt-2 pb-20 md:pt-4 md:pb-32 px-4 overflow-hidden" data-id="element-308">
      <div className="max-w-2xl mx-auto space-y-24" data-id="element-309">
        {/* PART A: THE PAIN */}
        <div className="space-y-8" data-id="element-310">
          <FadeIn data-id="element-311">
            <h2 className="font-serif text-3xl md:text-4xl leading-tight text-[#f5f0e8]" data-id="element-312">
              Hay un tipo de dolor en el trading que casi nadie admite en voz
              alta.
            </h2>
          </FadeIn>

          <FadeIn data-id="element-313">
            <div className="relative my-12 rounded-2xl overflow-hidden border border-[#2a2a2a] max-w-sm mx-auto" data-id="element-314">
              <img src="https://cdn.magicpatterns.com/uploads/8WTcEoFpLgjzmywUhaoyUy/Imagen_6.png" alt="¿Cómo es posible que alguien con mi misma estrategia gane mientras yo pierdo?" className="w-full h-auto object-cover opacity-90" loading="lazy" decoding="async" data-id="element-315" />
            </div>
          </FadeIn>

          <FadeIn data-id="element-316">
            <p className="text-lg text-[#8a8578] leading-relaxed" data-id="element-317">
              No es "perder dinero". Es esto: Terminar una sesión sabiendo que{' '}
              <strong className="text-[#f5f0e8]" data-id="element-318">no fuiste tú</strong> el que
              operó.
            </p>
          </FadeIn>

          <FadeIn data-id="element-319">
            <p className="text-lg text-[#8a8578] leading-relaxed" data-id="element-320">
              Que tu plan estaba claro. Que tus reglas estaban ahí. Que incluso{' '}
              <em data-id="element-321">sabías</em> lo que iba a pasar si te saltabas una sola… …y aun
              así lo hiciste.
            </p>
          </FadeIn>

          <FadeIn data-id="element-322">
            <div className="pl-6 border-l-2 border-[#d4af37] my-8 space-y-4 italic text-[#d4af37]/90" data-id="element-323">
              <p data-id="element-324">"Hoy ejecuto. Hoy soy serio. Hoy hago lo correcto."</p>
            </div>
          </FadeIn>

          <FadeIn data-id="element-325">
            <p className="text-lg text-[#8a8578] leading-relaxed" data-id="element-326">
              Pasa algo. Y de pronto estás persiguiendo el precio, moviendo el
              stop, aumentando tamaño, tomando trades mediocres, operando por
              rabia o por aburrimiento…
            </p>
            <p className="text-lg text-[#8a8578] leading-relaxed mt-4" data-id="element-327">
              …y al final, cuando se apaga la pantalla, vuelves a prometerte lo
              mismo: "Mañana sí."
            </p>
            <p className="text-lg text-[#f5f0e8] font-medium mt-4" data-id="element-328">
              Pero mañana, el mismo patrón te espera.
            </p>
          </FadeIn>

          <FadeIn data-id="element-329">
            <div className="relative my-12 rounded-2xl overflow-hidden border border-[#2a2a2a] max-w-sm mx-auto" data-id="element-330">
              <img src="https://cdn.magicpatterns.com/uploads/pn4RU8UKvbKWRFPYDoM748/Imagen_referencia_3.png" alt="Perdió todo, Otra Vez" className="w-full h-auto object-cover opacity-90" loading="lazy" decoding="async" data-id="element-331" />
            </div>
          </FadeIn>
        </div>

        {/* PART B: THE PROBLEM */}
        <div className="space-y-8" data-id="element-332">
          <FadeIn data-id="element-333">
            <h3 className="font-serif text-2xl md:text-3xl text-[#d4af37] mb-6" data-id="element-334">
              Lo que casi nadie te dijo: tu cuenta no la opera tu mente
              consciente
            </h3>
          </FadeIn>

          <FadeIn data-id="element-335">
            <p className="text-lg text-[#8a8578] leading-relaxed" data-id="element-336">
              Tu mente consciente es la que entiende estrategias. La que mira
              videos. Pero el trading real —el que define tu PnL— no se decide
              ahí. Se decide en la parte de ti que reacciona cuando hay presión.
              Esa parte es tu <strong data-id="element-337">subconsciente</strong>.
            </p>
          </FadeIn>

          <FadeIn data-id="element-338">
            <div className="bg-[#0a0a0a] border border-[#222] p-8 rounded-xl my-8" data-id="element-339">
              <p className="text-[#8a8578] mb-6 text-sm uppercase tracking-wider" data-id="element-340">
                Tu programa interno:
              </p>
              <ul className="space-y-3" data-id="element-341">
                {['Condicionamientos antiguos', 'Necesidad de control', "Necesidad de validar que 'sí puedes'", 'Miedo a quedar como tonto', 'Impulso de reparar dolor rápido', 'Hábito de buscar dopamina', 'Castigo silencioso'].map((item, i) => <li key={i} className="flex items-start gap-3 text-[#f5f0e8]" data-id="element-342">
                    <span className="text-[#d4af37] mt-1.5 h-1.5 w-1.5 rounded-full bg-[#d4af37] flex-shrink-0" data-id="element-343" />
                    <span data-id="element-344">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn data-id="element-345">
            <p className="text-xl md:text-2xl font-serif text-[#f5f0e8] text-center border-y border-[#222] py-8 my-8" data-id="element-346">
              Saber no es lo mismo que obedecer.
            </p>
          </FadeIn>
        </div>

        {/* PART C: IDENTITY */}
        <div className="space-y-8" data-id="element-347">
          <FadeIn data-id="element-348">
            <h3 className="font-serif text-2xl md:text-3xl text-[#f5f0e8]" data-id="element-349">
              El problema real: no estás construyendo identidad. Estás
              persiguiendo resultados.
            </h3>
          </FadeIn>

          <FadeIn data-id="element-350">
            <div className="relative my-12 rounded-2xl overflow-hidden border border-[#2a2a2a] max-w-sm mx-auto" data-id="element-351">
              <img src="https://cdn.magicpatterns.com/uploads/1tDbncz4WFvPK7mfUnmwZp/Imagen_9.png" alt="Perdía y perdía y cada pérdida le arrancaba un pedazo del alma" className="w-full h-auto object-cover opacity-90" loading="lazy" decoding="async" data-id="element-352" />
            </div>
          </FadeIn>

          <FadeIn data-id="element-353">
            <p className="text-lg text-[#8a8578] leading-relaxed" data-id="element-354">
              Si eres trader, seguramente admiras a alguien. No por su
              Lamborghini. Lo admiras porque se nota que esa persona opera con
              sobriedad. Con una calma que no es "zen"… sino profesional.
            </p>
          </FadeIn>

          <FadeIn data-id="element-355">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 my-12" data-id="element-356">
              <div className="space-y-4" data-id="element-357">
                <h4 className="text-[#d4af37] font-medium border-b border-[#d4af37]/30 pb-2 mb-4" data-id="element-358">
                  El Trader Rentable
                </h4>
                <ul className="space-y-3 text-sm text-[#f5f0e8]" data-id="element-359">
                  <li className="flex gap-2" data-id="element-360">
                    <span className="text-green-500" data-id="element-361">✓</span> No negocia reglas
                    con emoción
                  </li>
                  <li className="flex gap-2" data-id="element-362">
                    <span className="text-green-500" data-id="element-363">✓</span> No convierte el
                    trading en terapia
                  </li>
                  <li className="flex gap-2" data-id="element-364">
                    <span className="text-green-500" data-id="element-365">✓</span> No opera para
                    sentirse poderoso
                  </li>
                  <li className="flex gap-2" data-id="element-366">
                    <span className="text-green-500" data-id="element-367">✓</span> No reacciona con
                    rabia
                  </li>
                </ul>
              </div>
              <div className="space-y-4 opacity-60" data-id="element-368">
                <h4 className="text-[#8a8578] font-medium border-b border-[#8a8578]/30 pb-2 mb-4" data-id="element-369">
                  La Identidad Fracturada
                </h4>
                <div className="space-y-4 text-sm" data-id="element-370">
                  <div data-id="element-371">
                    <p className="text-[#f5f0e8] font-medium mb-1" data-id="element-372">En frío:</p>
                    <p className="italic" data-id="element-373">
                      "Soy disciplinado. Soy serio. Sé lo que hago."
                    </p>
                  </div>
                  <div data-id="element-374">
                    <p className="text-[#f5f0e8] font-medium mb-1" data-id="element-375">
                      En caliente:
                    </p>
                    <p className="italic" data-id="element-376">
                      "Solo hoy… solo este trade… solo para recuperar…"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* IMAGE: Fractured identity pain */}
          <FadeIn data-id="element-377">
            <div className="relative rounded-2xl overflow-hidden border border-[#d4af37]/30 shadow-[0_0_40px_rgba(212,175,55,0.1)] max-w-sm mx-auto my-16" data-id="element-378">
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent z-10" data-id="element-379"></div>
              <img src="https://cdn.magicpatterns.com/uploads/2UeM2i8KsGrZfK2w2Sm4Fr/Imagen_17.png" alt="Quería controlarlo todo... pero no podía controlarse a él" className="w-full h-auto object-cover opacity-90" loading="lazy" decoding="async" data-id="element-380" />
            </div>
          </FadeIn>
        </div>

        {/* PART D: THE MARKET */}
        <div className="space-y-8" data-id="element-381">
          <FadeIn data-id="element-382">
            <h3 className="font-serif text-2xl md:text-3xl text-[#f5f0e8]" data-id="element-383">
              El mercado no te destruye. <br data-id="element-384" />
              <span className="text-[#d4af37]" data-id="element-385">
                El mercado activa tu programación.
              </span>
            </h3>
          </FadeIn>

          <FadeIn data-id="element-386">
            <div className="relative my-12 rounded-2xl overflow-hidden border border-[#2a2a2a] max-w-sm mx-auto" data-id="element-387">
              <img src="https://cdn.magicpatterns.com/uploads/jGoHBUEcfHHq9FZVXYJHSY/Imagen_15.png" alt="No era el mercado, era yo, peleando conmigo mismo" className="w-full h-auto object-cover opacity-90" loading="lazy" decoding="async" data-id="element-388" />
            </div>
          </FadeIn>

          <FadeIn data-id="element-389">
            <p className="text-lg text-[#8a8578] leading-relaxed" data-id="element-390">
              El mercado no es personal. Pero tú sí lo vuelves personal cuando
              interpretas la información como:
            </p>
          </FadeIn>

          <FadeIn data-id="element-391">
            <div className="flex flex-wrap gap-3 my-6" data-id="element-392">
              {['me quitó algo', 'me está probando', 'me está humillando', 'me debe', 'tengo que demostrar'].map((item, i) => <span key={i} className="px-4 py-2 bg-[#1a1a1a] border border-[#333] rounded-full text-[#d4af37] text-sm italic" data-id="element-393">
                  "{item}"
                </span>)}
            </div>
          </FadeIn>

          <FadeIn data-id="element-394">
            <p className="text-lg text-[#8a8578] leading-relaxed" data-id="element-395">
              Ese es el momento donde dejas de ser trader… y te conviertes en
              alguien que está buscando regular emociones con clicks. Y eso crea
              venganza, sobreoperación y miedo.
            </p>
          </FadeIn>

          <FadeIn data-id="element-396">
            <div className="p-6 border-l-4 border-[#d4af37] bg-gradient-to-r from-[#d4af37]/10 to-transparent mt-12" data-id="element-397">
              <p className="text-2xl font-serif text-[#f5f0e8]" data-id="element-398">
                No es falta de conocimiento. <br data-id="element-399" />
                Es falta de sistema interno.
              </p>
            </div>
          </FadeIn>

          {/* THE REALIZATION (BRIDGE TO PRODUCT) */}
          <FadeIn data-id="element-400">
            <div className="relative rounded-2xl overflow-hidden border border-[#00C853]/30 shadow-[0_0_40px_rgba(0,200,83,0.1)] max-w-sm mx-auto mt-16" data-id="element-401">
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent z-10" data-id="element-402"></div>
              <img src="https://cdn.magicpatterns.com/uploads/hcB3fHHg7kC4TzyRduXdpM/Generated_Image_February_21,_2026_-_6_18PM.jpg" alt="Entendió que no era la estrategia, era él" className="w-full h-auto object-cover opacity-90" loading="lazy" decoding="async" data-id="element-403" />
            </div>
          </FadeIn>

          {/* THE NEW IDENTITY */}
          <FadeIn data-id="element-404">
            <div className="relative rounded-2xl overflow-hidden border border-[#d4af37]/30 shadow-[0_0_40px_rgba(212,175,55,0.1)] max-w-sm mx-auto mt-12" data-id="element-405">
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent z-10" data-id="element-406"></div>
              <img src="https://cdn.magicpatterns.com/uploads/2RP3vHJCDCy5jR3WmUP5FR/Imagen_12.png" alt="Ya no reacciono porque ya sé quien soy" className="w-full h-auto object-cover opacity-90" loading="lazy" decoding="async" data-id="element-407" />
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}